import L, { LayerOptions, Map, Point, ZoomAnimEvent } from 'leaflet';
import { HeatmapCanvas, LatLngIntensity } from '../visualisation/HeatmapCanvas';

export class HeatmapLayer extends L.Layer {
  private readonly _latLngs: LatLngIntensity[];
  private readonly _cellWidth: number;
  private _heatmap: HeatmapCanvas | null;

  static MIN_CELL_WIDTH = 0.000001; // An extreme zoomed minimum

  constructor(latLngs: LatLngIntensity[], cellWidth?: number, options?: LayerOptions) {
    super(options);

    this._heatmap = null;
    this._latLngs = latLngs;
    const safeCellWidth =
      cellWidth && cellWidth > HeatmapLayer.MIN_CELL_WIDTH
        ? cellWidth
        : HeatmapLayer.MIN_CELL_WIDTH;
    const bleedFactor = 1.25;
    this._cellWidth = safeCellWidth * bleedFactor;

    L.setOptions(this, options);
  }

  public onAdd(map: Map): this {
    this._map = map;

    if (!this._heatmap) {
      this._initCanvas();
    }

    if (this._heatmap) {
      if (this.options.pane) {
        this.getPane()?.appendChild(this._heatmap.canvas);
      } else {
        map.getPanes().overlayPane.appendChild(this._heatmap.canvas);
      }
    }

    map.on('moveend', this._reset, this);
    // Triggers when the user initiates a zoom level change.
    map.on('zoomanim', this._animateZoom, this);

    this._reset();

    return this;
  }

  public onRemove(map: Map): this {
    if (this._heatmap) {
      if (this.options.pane) {
        this.getPane()?.removeChild(this._heatmap.canvas);
      } else {
        map.getPanes().overlayPane.removeChild(this._heatmap.canvas);
      }
    }

    map.off('moveend', this._reset, this);
    map.off('zoomanim', this._animateZoom, this);

    return this;
  }

  private _initCanvas(): void {
    const size = this._map.getSize();

    this._heatmap = new HeatmapCanvas({
      width: size.x,
      height: size.y,
      cellWidth: this._getRelativeCellWidth(),
      className: 'leaflet-heatmap-layer leaflet-layer leaflet-zoom-animated',
    });

    this._heatmap.canvas.style.transformOrigin = '50% 50%';
  }

  private _reset(): void {
    if (this._heatmap) {
      // Reposition the heatmap canvas relative to the map.
      const topLeft = this._map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(this._heatmap.canvas, topLeft);

      // Update the heatmap canvas size.
      const size = this._map.getSize();

      if (this._heatmap.getWidth() !== size.x) {
        this._heatmap.setWidth(size.x);
      }

      if (this._heatmap.getHeight() !== size.y) {
        this._heatmap.setHeight(size.y);
      }

      // Update the cell size
      this._heatmap.setCellWidth(this._getRelativeCellWidth());

      this._redraw();
    }
  }

  // Get the cell width in pixels relative to the current zoom level of the map.
  private _getRelativeCellWidth(): number {
    const origin: Point = this._map.latLngToContainerPoint([0, 0]);
    const cellWidthOffset: Point = this._map.latLngToContainerPoint([0, this._cellWidth]);
    const pixelWidth = Math.abs(origin.x - cellWidthOffset.x);

    return pixelWidth;
  }

  private _redraw(): void {
    if (!this._map || !this._heatmap) {
      return;
    }

    const data: LatLngIntensity[] = [];
    const diameter = this._heatmap.getCellWidth();
    const r = diameter / 2;

    const size = diameter / 4;
    const mapSize = this._map.getSize();
    const bounds = new L.Bounds(L.point([-r, -r]), mapSize.add([r, r]));

    const grid: [number, number, number][][] = [];
    const panePos = this._map._getMapPanePos();
    const offsetX = panePos.x % size;
    const offsetY = panePos.y % size;

    for (let i = 0, len = this._latLngs.length; i < len; i++) {
      const p: Point = this._map.latLngToContainerPoint(this._latLngs[i]);

      if (bounds.contains(p)) {
        const x = Math.floor((p.x - offsetX) / size) + 2;
        const y = Math.floor((p.y - offsetY) / size) + 2;

        const intensity = this._latLngs[i][2];

        grid[y] = grid[y] || [];

        const cell = grid[y][x];

        if (!cell) {
          grid[y][x] = [p.x, p.y, intensity];
        } else {
          cell[0] = (cell[0] * cell[2] + p.x * intensity) / (cell[2] + intensity); // x
          cell[1] = (cell[1] * cell[2] + p.y * intensity) / (cell[2] + intensity); // y
          cell[2] += intensity; // cumulative intensity value
        }
      }
    }

    for (let i = 0, len = grid.length; i < len; i++) {
      if (grid[i]) {
        for (let j = 0, len2 = grid[i].length; j < len2; j++) {
          const cell = grid[i][j];

          if (cell) {
            data.push([
              Math.round(cell[0]),
              Math.round(cell[1]),
              Math.min(cell[2], HeatmapCanvas.MAX_INTENSITY),
            ]);
          }
        }
      }
    }

    this._heatmap.setData(data);
    this._heatmap.draw();
  }

  private _animateZoom(e: ZoomAnimEvent): void {
    if (this._heatmap) {
      const scale = this._map.getZoomScale(e.zoom);
      const offset = this._map
        ._getCenterOffset(e.center)
        .multiplyBy(-scale)
        .subtract(this._map._getMapPanePos());

      L.DomUtil.setTransform(this._heatmap.canvas, offset, scale);
    }
  }
}

export default HeatmapLayer;
